import { Outlet, useNavigate } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { UsageIndicator } from '@/components/layout/UsageIndicator';
import { TopMenuBar } from '@/components/layout/TopMenuBar';
import { useAuthStore } from '@/store/authStore';
import { cognitoAuth } from '@/services/auth/cognito';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  ChevronsUpDown, Network, Users, Link2, CreditCard, Settings, LogOut, DollarSign,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export function AdminLayout() {
  const { isSuperAdmin } = useAuthStore();

  // Super admin still uses the old sidebar (platform management)
  if (isSuperAdmin()) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <div className="flex-1" />
            <UsageIndicator />
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Regular firm users: new top menu bar (Word-style)
  return (
    <div>
      <TopMenuBar />
      {/* Right-side actions overlay - fixed to top-right */}
      <div className="fixed top-0 right-4 h-11 flex items-center gap-3 z-50">
        <UsageIndicator />
        <UserMenu />
      </div>

      {/* Page content */}
      <main className="p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}

function UserMenu() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();

  const getInitials = (email: string) => email.substring(0, 2).toUpperCase();

  const handleLogout = () => {
    cognitoAuth.signOut();
    queryClient.clear();
    logout();
    window.location.href = '/login';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="outline-none">
        <div className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted cursor-pointer">
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-[#5048e5]/10 text-[#5048e5] text-xs">
              {user?.email ? getInitials(user.email) : 'AD'}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:flex flex-col text-left">
            <span className="text-xs font-semibold leading-tight">{user?.email?.split('@')[0] || 'User'}</span>
            {user?.role === 'tenant_admin' && (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 w-fit">Admin</Badge>
            )}
          </div>
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/panel/organization')}>
          <Network className="h-4 w-4 mr-2" /> Organization
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/users')}>
          <Users className="h-4 w-4 mr-2" /> Users
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/integrations/xero')}>
          <Link2 className="h-4 w-4 mr-2" /> Integrations
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/billing')}>
          <CreditCard className="h-4 w-4 mr-2" /> Billing & Plan
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/settings')}>
          <Settings className="h-4 w-4 mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
